import type { LevelDef } from './types';

/**
 * Day 1 — Friday, after school. An ordinary walk home from Yoruno Station at
 * dusk: nobody follows her. It teaches the walk, the map, the phone and Mom,
 * and shows the streets in daylight before the nights take them away.
 */
export const night1: LevelDef = {
  id: 'night1',
  night: 1,
  title: 'After School',
  subtitle: 'Friday · 16:20',
  look: 'school',
  card: ['School\'s out for the weekend.', 'The train home, then the walk from Yoruno Station.', 'Mom is making curry tonight.'],
  clockStart: 16 * 60 + 20,
  calm: true,
  // North (row 0) is home. She starts at the bottom, near the station.
  map: [
    '#####################', // 0
    '########H############', // 1
    '###....B....#########', // 2
    '###.#######.#########', // 3
    '###.#######L#########', // 4
    '###.#######....A#####', // 5
    '###.##########.######', // 6
    '###.##########.######', // 7
    '###.##########T######', // 8
    '###.##########L######', // 9
    '##......L....L.....##', // 10  zone 3: the rule flips
    '###.##p###.##########', // 11
    '###.##p###X##########', // 12
    '###.##p###L##########', // 13
    '###.##p###.######V###', // 14
    '###F##pppG.######.###', // 15
    '###.######.######L###', // 16
    '###.######F######.###', // 17
    '###.######L######V###', // 18
    '###.######.######.###', // 19
    '##....L...L...L....##', // 20  zone 2: the rule breaks
    '##########.##########', // 21
    '##########.C#########', // 22
    '##########.##########', // 23
    '##########L##########', // 24
    '##########.##########', // 25
    '##########.##########', // 26
    '##########.##########', // 27
    '##########L##########', // 28
    '##########V##########', // 29
    '##########.##########', // 30
    '##########S##########', // 31
    '##########.##########', // 32
    '##########.##########', // 33
    '##########P##########', // 34
    '##########E##########', // 35
  ],
  // Nobody follows her today, so the light rules don't apply.
  zones: [{ zone: 1 }],
  battery: 76,
  theme: {
    sky: '#c98a6c',
    fog: { color: '#b88672', density: 0.02 },
    hemi: { sky: '#d8c0b0', ground: '#3b2e26', intensity: 1.35 },
    ambient: 0.85,
    moon: 1.7,
    keyColor: '#ffc08a',
    daylight: 0.6,
  },
  stranger: { walkMax: 2.8, chaseSpeed: 3.0, gapMax: 40, spawnDelay: 5 },
  cat: {
    home: [10.7, 15.78],
    path: [
      [10.1, 15.5],
      [6.5, 15.5],
      [6.5, 12.5],
    ],
    line: 'cat',
  },
  lines: {
    intro: 'Weekend. Home, shower, curry. In that order.',
    konbini: 'The konbini. Ice cream? …No. Curry.',
    dark1: 'This street is always so quiet.',
    act2: 'The map says straight on is quickest.',
    roadworks: 'Road works. The map didn\'t say. I have to go back.',
    deadend: 'A dead end. Great.',
    cat: 'A cat! …and it\'s gone, down the shrine path.',
    path: 'The shrine path. It cuts straight through.',
    pathExitGone: 'Out the other side. That really was quicker.',
    sunset: "The sun's going down. Home before dark.",
    wrongBuilding: 'Maison Sakura… no. Mine\'s the next one.',
    homeStretch: 'Almost there. Keys… got them.',
    momIgnored: 'I\'ll text her back in a minute.',
    momIgnoredAgain: 'Mom again. I\'ll answer when I\'m in.',
    darkSlow: 'Hard to see down here. My phone has a flashlight.',
  },
  endings: {
    home: {
      title: 'Home.',
      lines: ['Shoes off at the door. Curry on the stove.', 'Mom asks about the test. You say it went fine.', 'An ordinary Friday.'],
      hint: 'Next: a late shift on Tuesday, in the rain.',
    },
  },
  mom: {
    first: { node: 'start', at: 6 },
    script: {
      start: {
        text: 'How was the maths test?',
        options: [
          { text: 'Fine I think', end: 'Good! Curry tonight 🍛' },
          { text: "Don't ask", next: 'test' },
        ],
      },
      test: {
        text: 'That bad? We can talk at dinner.',
        options: [
          { text: 'Ok', end: 'Come straight home, ok?' },
          { text: 'Can I go to karaoke Saturday?', next: 'karaoke' },
        ],
      },
      karaoke: {
        text: "With Yui? Fine, if you're home by 9.",
        options: [
          { text: 'Thank you!!', end: '❤️' },
          { text: 'By 10?', end: '9.' },
        ],
      },
      act2: {
        text: 'Can you get milk on the way?',
        options: [
          { text: 'Ok', end: 'Thank you!' },
          { text: "I'm already past the konbini", end: 'Never mind then.' },
        ],
      },
      act3: {
        text: 'Are you close? Dinner is almost ready.',
        options: [
          { text: 'Five minutes', end: 'Ok 🍛' },
          { text: 'Starving', end: 'Then hurry!' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '10,22' || key === '10,23') ctx.sayOnce('konbini');
    if (key === '10,26') ctx.sayOnce('dark1');
    if (key === '10,13') ctx.sayOnce('roadworks');
    if (key === '17,14') ctx.sayOnce('deadend');
    // Halfway: the cross street where Mom asks for milk.
    if (cell.r === 20 && !ctx.said('act2')) {
      ctx.sayOnce('act2');
      ctx.momLater('act2', 3);
    }
    if (cell.r === 10 && !ctx.said('sunset')) {
      ctx.sayOnce('sunset');
      ctx.notify('yui', 'Yui', 'karaoke sat??? say yes 🎤🎤', 'normal', 10);
      ctx.momLater('act3', 8);
    }
    if (cell.r === 2) ctx.sayOnce('homeStretch');
  },
};
